import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${window.location.origin}/api`;

// Roles / permissions that can access the support inbox
function canAccessSupport(user) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (['manager', 'customer_service', 'accountant'].includes(user.role)) {
    return !!user?.permissions?.support_chat?.can_view;
  }
  return false;
}

// Shared hook: polls the staff support unread count and reacts to live events.
export default function useSupportUnread() {
  const { user, token } = useAuth();
  const [unread, setUnread] = useState(0);
  const enabled = canAccessSupport(user);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const refresh = useCallback(async () => {
    if (!enabledRef.current) return;
    try {
      const res = await axios.get(`${API}/support-chat/unread-count`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` },
      });
      setUnread(res.data?.unread || 0);
    } catch (e) { /* ignore */ }
  }, [token]);

  useEffect(() => {
    if (!enabled) { setUnread(0); return; }
    refresh();
    const t = setInterval(refresh, 15000);
    // Live bump when AdminAlertBell's websocket receives a support message,
    // and refresh when the inbox marks messages read.
    const onMsg = () => refresh();
    window.addEventListener('support-message', onMsg);
    window.addEventListener('support-unread-refresh', onMsg);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener('support-message', onMsg);
      window.removeEventListener('support-unread-refresh', onMsg);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, refresh]);

  return { unread, refresh, enabled };
}
